import { createSelector } from "@reduxjs/toolkit";

import { findTerminalExamBeat } from "../../components/Dashboard/CredentialJourney/terminalExam";
import {
  deriveJourneyStep,
  JOURNEY_STEP,
  journeyIsSettled,
} from "../../pages/Dashboard/deriveJourneyStep";
import {
  deriveCredentialEffectiveStatus,
  isCredentialLevelComplete,
  JOURNEY_VARIANT,
  resolveJourneyVariant,
} from "../../pages/Dashboard/resolveJourneyVariant";
import { usesV4Chain } from "../../pages/Dashboard/resolveTrainingEntry";
import { coerceCapsuleId, sameCapsuleId } from "../../utils/capsuleId";
import { economyScopeKey, outputCapsuleIdOf } from "../economy/slice";
import { PROJECTION_STALE_TTL_MS } from "../experienceProjection/slice";
import { shipWalkthroughScopeKey } from "../shipWalkthrough/slice";
import {
  isBoundaryMission,
  statedBoundaryCapsuleIdOf,
} from "../user/missionBoundary";
import { selectActiveCohortUser } from "../user/slice";

/**
 * The read side of the shared experience projection.
 *
 * ONE OWNER PER FACT. The projection slice owns squad-shared submission facts
 * and nothing else. Everything else these selectors need is read THROUGH its
 * existing owner and passed on by reference, never copied into a second home:
 *
 *   state.user.status              user slice (store/user/slice.js:20)
 *   state.user.mission             user slice, per-learner position authority,
 *                                  including the client advance pin (:237-284)
 *   state.tasks.squadUsers         tasks slice (store/tasks/slice.js:110)
 *   state.planTasks.sessionTasks   planTasks slice (store/planTasks/slice.js:310)
 *   state.credentialClearance      credentialClearance slice
 *
 * SQUAD MEMBERSHIP IS HISTORICAL TODAY, NOT CURRENT. Verified on the backend
 * 2026-09-01, and stated plainly because an earlier version of this header
 * claimed the opposite and would have sent a consumer building on a false
 * premise. `state.tasks.squadUsers` is filled by `fetchSquadUsersAndQuests`
 * (store/tasks/slice.js:5-14), which reads `GET /squad-users/?squad_id=X`.
 * `SquadUserAPIController::index` calls `BaseRepository::all()` with NO
 * `current()` scope, and `SquadUser::scopeCurrent` is a deliberately NAMED
 * scope rather than a global one, so superseded rows are returned. Nor can the
 * client filter them out: `SquadUserResource::toArray` emits id, squad_id,
 * user_id, points, avatar_id, timestamps, metrics and relations, and NO
 * `superseded_at` marker. So the frontend currently cannot distinguish a
 * current member from a moved-out one at all.
 *
 * These selectors therefore do no membership arithmetic. They pass the
 * collection through by reference, so nothing here silently drops or
 * double-counts a moved-out member's work. `selectCurrentSquadUsers` below is
 * the one place that will start filtering the moment the backend follow-on
 * (ruled by Jenni: `SquadUserResource` emits `superseded_at` additively) lands,
 * with no further frontend change required.
 *
 * NO COPY. `selectNextAction` (added by Chip P3, bottom of this file) is the ONE
 * public next-move selector. It delegates the decision to deriveJourneyStep and
 * nothing else may re-derive the learner's next move: the Dashboard step cascade
 * and the re-entry context line read the SAME decision object.
 */

// A stable empty array. Returning a fresh [] on every call would make every
// useSelector consumer re-render on every store action.
const EMPTY_OUTPUTS = Object.freeze([]);
const EMPTY_TRI_STATE = Object.freeze({});

export const TRI_STATE = Object.freeze({
  UPCOMING: "upcoming",
  CURRENT: "current",
  SUBMITTED: "submitted",
});

const selectProjectionState = (state) => state.experienceProjection;

/** The ordered output list exactly as the server sent it. */
export const selectAllOutputs = (state) =>
  state.experienceProjection.projection?.outputs ?? EMPTY_OUTPUTS;

/** The keyed index the slice built. Keys are coerced capsule ids. */
export const selectOutputsByCapsuleId = (state) =>
  state.experienceProjection.outputsByCapsuleId;

/** The projection's error, or null. `kind` is "unauthorized" (404) or "unavailable". */
export const selectProjectionError = (state) =>
  state.experienceProjection.error;

/**
 * Is the held snapshot stale enough to refetch?
 *
 * Two ways in. (a) The slice marked it: a submission settled, on either arm.
 * (b) The TTL: the SERVER's generated_at is older than PROJECTION_STALE_TTL_MS.
 *
 * The TTL arm carries two guards, and both are load bearing.
 *
 * It applies only to a SUCCEEDED snapshot. If a failed read could go stale by
 * the clock, the Dashboard refetch effect would dispatch, fail, and immediately
 * qualify again: a network-speed retry loop. After a failure the retry is a
 * learner action, not a clock event.
 *
 * It also requires that the client has HELD the snapshot for at least the same
 * interval. generated_at is the server's clock and Date.now() is the browser's;
 * a server running more than the TTL behind the browser would make every fresh
 * response instantly stale and produce the same loop. The fetchedAt floor is a
 * same-clock comparison, so it cannot. generated_at still governs WHEN
 * staleness may fire, exactly as the contract states.
 */
export const selectIsStale = (state) => {
  const projection = state.experienceProjection;
  if (projection.stale) return true;
  if (projection.status !== "succeeded") return false;
  const generatedAtMs = Date.parse(projection.generatedAt ?? "");
  if (Number.isNaN(generatedAtMs)) return false;
  const now = Date.now();
  if (now - generatedAtMs <= PROJECTION_STALE_TTL_MS) return false;
  return (
    projection.fetchedAt !== null &&
    now - projection.fetchedAt > PROJECTION_STALE_TTL_MS
  );
};

/**
 * The learner's current capsule, from the mission. The mission slice is the
 * SOLE owner of position (RULING R1); the projection has no opinion about it.
 */
const selectMission = (state) => state.user.mission;

/**
 * WHICH CAPSULE THE LEARNER IS ON. ONE DEFINITION, READ BY EVERY SURFACE.
 *
 * The mission is the SOLE owner of the learner's position (RULING R1). The
 * fallback from the lesson to the module is not defensive: `/users/mission`
 * serves `lesson` as the newest `user_lessons` row and `module` as the
 * program's first module when there is none, so a learner who has not opened a
 * briefing yet carries only the module and has a position like anybody else.
 *
 * IT IS DECLARED HERE AND NOT BESIDE ITS SECOND CALLER because the two callers
 * disagreeing is a shipped defect rather than a hypothetical one. W4-F2.6,
 * F48: `selectJourneyInputs` asked this question through this function and put
 * a first-level learner on the rail; `selectCurrentCapsuleId` below asked it by
 * reading `mission.lesson.capsule_id` alone, answered null for the same
 * learner, and `selectCurrentOutput` therefore had no row, so the band's
 * `this level's output` label was absent through kickoff, mission, briefings
 * and `your take` and appeared on a hard refresh -- by which time the learner
 * HAD a `user_lessons` row and the lesson-only spelling finally agreed with the
 * module one. Three of Amir's screens on 2026-09-16, at 0 percent and at 19
 * percent, and the same 19 percent after the refresh.
 *
 * ONCE A LESSON EXISTS THE LESSON WINS, so this is inert for every learner who
 * has opened anything: the only world it changes is the one before the first
 * lesson, where the alternative was no answer at all.
 *
 * AND THE MODULE IS THE LAST RESORT, NOT THE SECOND ONE (W4-F2.6 fix batch item
 * 1, Codex cross-review MAJOR). `getMission` serves the module as
 * `Module::where('program_id', $programId)->first()`: the program's FIRST
 * module, with no level, squad or progression filter, and it serves
 * `lesson: null` for a learner who has never opened a lesson at ANY level, not
 * merely for one who is about to open their first. A squad member who never
 * personally engages stays in that population while their squad ships level 1
 * and moves to level 2, and the module alone then answers "level 1" for them
 * forever. The first F48 fix therefore turned a silent gap into a confident
 * wrong answer: level 1's SUBMITTED artifact painted as `this level's output`
 * with a green check, on a squad whose open level is 2.
 *
 * SO THE SQUAD'S OWN LEVEL IS ASKED FIRST. `selectSquadCurrentCapsuleId` below
 * is that question with one definition; it answers null on a cohort where
 * nothing says, and only then does the module answer.
 *
 * AT A LEVEL BOUNDARY THE MISSION NAMES NEITHER, and the answer is
 * `selectBoundaryCapsuleId` below: the level the learner has not yet walked
 * past Ship on, until the learner's own advance moves them to the capsule the
 * server states. See that selector for why.
 *
 * @param {object|null} mission `state.user.mission`
 * @param {number|null} squadCapsuleId the squad's current capsule, or null
 * @param {number|string|null|undefined} boundaryCapsuleId
 *   `selectBoundaryCapsuleId`'s answer, read only when the mission names
 *   neither a lesson nor a module
 * @returns {number|string|null|undefined}
 */
const decidedCapsuleId = (
  mission,
  squadCapsuleId = null,
  boundaryCapsuleId = undefined,
) => {
  /* NO MISSION IS NO POSITION, and the squad's level does not repair that. The
     mission is the SOLE owner of the learner's position (RULING R1): the squad
     arm below REFINES a position the mission gave, it does not invent one for a
     learner the mission cannot place at all. That is what the band's negative
     control pins here, and what `Dashboard.economy.test.jsx`'s F35 case pins on
     the page. */
  if (!mission) return undefined;
  if (mission.lesson) return mission.lesson?.capsule_id;
  /* NEITHER A LESSON NOR A MODULE IS A LEVEL BOUNDARY. The squad refinement
     below is not applied to it: that refinement exists because the module is
     the program's FIRST module for a lesson-less learner, and a boundary
     payload states a real position. */
  if (isBoundaryMission(mission)) return boundaryCapsuleId;
  const fromModule = mission.module.capsule_id;
  /* A MODULE THAT CARRIES NO CAPSULE KEEPS ITS OWN ANSWER, for the same reason
     a missing mission does. */
  if (fromModule === null || fromModule === undefined) return fromModule;
  return squadCapsuleId ?? fromModule;
};

/**
 * THE CAPSULE THE LEARNER FINISHED: THE EXACT INVERSE OF THE SERVER'S
 * SUCCESSOR RULE.
 *
 * `PersonalMissionService::read` (line 104) moves a learner who finished
 * capsule P to the capsule of the FIRST module, ordered by module id, whose
 * `capsule_id` is greater than P. So the finished capsule is the P whose
 * successor, computed the same way over the same modules, is the stated
 * capsule. Neither "the previous week" nor "the nearest lower id" is that
 * inverse once module ids and capsule ids disagree in order.
 *
 * WHEN MORE THAN ONE P HAS THAT SUCCESSOR, the server's own reason for the
 * move decides: it moves the position only once the learner's progress report
 * for P is in (`hasReport`), so a candidate is kept only where the learner's
 * own `capsule_users` row says `milestone_report_submitted`. One survivor is
 * the answer; none or several is no answer.
 *
 * FAIL CLOSED. `undefined` when the capsule list is not loaded, when any
 * capsule's `modules` are not loaded (the successor cannot be computed over a
 * partial module list), or when no single P qualifies. The caller then decides
 * nothing; it never falls back to the stated capsule.
 *
 * Raw, as `capsules[]` carries it, which is the type `Dashboard.jsx` compares
 * with `===`.
 *
 * @param {Array<object>|undefined} capsules `state.dashboard.capsules`
 * @param {number|string} statedCapsuleId
 * @param {Array<object>|null|undefined} capsuleUsers the learner's own rows
 * @returns {number|string|undefined}
 */
const finishedCapsuleIdOf = (capsules, statedCapsuleId, capsuleUsers) => {
  if (!Array.isArray(capsules) || capsules.length === 0) return undefined;
  const stated = coerceCapsuleId(statedCapsuleId);
  if (stated === null) return undefined;
  if (capsules.some((capsule) => !Array.isArray(capsule?.modules))) {
    return undefined;
  }
  const modules = capsules
    .flatMap((capsule) =>
      capsule.modules.map((module) => ({
        id: Number(module?.id),
        capsuleId: coerceCapsuleId(capsule.id),
      })),
    )
    .filter((module) => Number.isFinite(module.id) && module.capsuleId !== null)
    .sort((a, b) => a.id - b.id);
  const successorOf = (position) =>
    modules.find((module) => module.capsuleId > position)?.capsuleId ?? null;
  const candidates = capsules.filter((capsule) => {
    const position = coerceCapsuleId(capsule?.id);
    return position !== null && successorOf(position) === stated;
  });
  if (candidates.length === 1) return candidates[0].id;
  if (candidates.length === 0) return undefined;
  const reported = candidates.filter((capsule) =>
    (capsuleUsers ?? []).some(
      (row) =>
        sameCapsuleId(row?.capsule_id, capsule.id) &&
        (row?.milestone_report_submitted === true ||
          Number(row?.milestone_report_submitted) === 1),
    ),
  );
  return reported.length === 1 ? reported[0].id : undefined;
};

/**
 * WHERE A LEARNER STANDS AT A LEVEL BOUNDARY (production, 2026-10-12).
 *
 * WHAT THE SERVER SENDS. `GET /users/mission-context`
 * (`PersonalMissionService::read`) moves the position to the next capsule once
 * a level is done on ITS terms (the lessons, the learner's quests and their
 * progress report), which is BEFORE Ship. It then omits the previous level's
 * `lesson`, and sends no `module` because a lesson row exists, and it states
 * the new position as `context.capsule_id` (an integer), with `nextLesson` and
 * `nextCapsule` rooted at the same capsule. The legacy `/users/mission` kept
 * `lesson` on the finished level and sent `nextCapsule` beside it, which is
 * what held the dashboard there until the learner's own Ship.
 *
 * THE RULING. Amir, 2026-10-12: "A person doesn't proceed to the next level
 * until they've proceeded past their ship step." A squadmate's submission shows
 * on the Ship step; it never moves this learner. That has always been the
 * ladder's own rule (`deriveJourneyStep`'s `pastOwnShip`, and its `terminal()`
 * advance), so this selector does not decide passage a second time. It puts
 * the learner where the ladder can decide it: on the capsule before the
 * stated one (the inverse of the server's successor rule), where the
 * ladder offers Ship, or advances them at once when the
 * learner's own ship record already exists.
 *
 * THE ADVANCE IS WHAT MOVES THEM, exactly as it was. `updateMissionCapsuleId`
 * and `advanceCredentialLevel` cannot rewrite a lesson the payload does not
 * carry, so at a boundary they record the transition as `missionAdvancePin`,
 * and a pin whose destination is the stated capsule is what this selector
 * reads as "advanced". The pin is in memory only, so a reload starts on the
 * previous capsule again and the ladder re-advances from the ship record, which
 * is the legacy endpoint's behaviour on a reload byte for byte.
 *
 * WITHOUT THIS the learner was placed on nothing: the Dashboard's
 * `capsules.find` missed, `capsule?.week || 1` printed a blank `LEVEL`, `week
 * 1` and the level 1 kickoff.
 *
 * `undefined` when the mission is not at a boundary (the caller never reads it
 * then), when it states no position, or when `finishedCapsuleIdOf` cannot
 * name exactly one finished capsule, so the store places nobody rather than
 * placing them wrongly or past a Ship step.
 *
 * @param {object} state the whole store
 * @returns {number|string|null|undefined}
 */
const selectBoundaryCapsuleId = (state) => {
  const mission = state?.user?.mission;
  if (!isBoundaryMission(mission)) return undefined;
  const stated = statedBoundaryCapsuleIdOf(mission);
  if (stated === null || stated === undefined) return stated;
  if (sameCapsuleId(state?.user?.missionAdvancePin?.to, stated)) return stated;
  /* FAIL CLOSED. With no finished capsule to hold the learner on, nothing is
     decided: answering the stated capsule would carry them past a Ship step
     they may not have walked. */
  return finishedCapsuleIdOf(
    state?.dashboard?.capsules,
    stated,
    state?.user?.capsuleUsers,
  );
};

/**
 * IS THE JOURNEY DECISION ABOUT A CAPSULE HELD AT A BOUNDARY, rather than the
 * one the mission states? True only while the learner has not advanced.
 *
 * @param {object|null|undefined} mission
 * @param {number|string|null|undefined} boundaryCapsuleId
 * @returns {boolean}
 */
const heldAtBoundaryOf = (mission, boundaryCapsuleId) =>
  isBoundaryMission(mission) &&
  boundaryCapsuleId !== null &&
  boundaryCapsuleId !== undefined &&
  !sameCapsuleId(boundaryCapsuleId, statedBoundaryCapsuleIdOf(mission));

/**
 * WHICH CAPSULE THE SQUAD IS ON, FROM THE SERVER WHERE THE SERVER SAYS SO.
 *
 * TWO ARMS, IN THIS ORDER, AND THE FIRST IS THE SERVER'S OWN SENTENCE.
 *
 *   `state.economy.output.capsule_id` on a versioned cohort. `EconomyReadDTO`
 *   resolves it as the capsule the squad most recently ENTERED and serves the
 *   output block for it; `outputCapsuleIdOf` is the slice's own reader for that
 *   field, imported rather than re-spelled, because the slice uses the same
 *   field to refuse a foreign floor result and two readers of one field is two
 *   answers waiting to disagree.
 *
 *   The projection's first unsubmitted output otherwise. That is a derivation
 *   and it is named as one: the projection carries no server-stated current
 *   level (its slice header says why), so this is the client asking "which
 *   level has this squad not shipped yet" of an ordered list the server built
 *   from the cohort's capsules. It is strictly better than the program's first
 *   module, which is what it replaces, and it is only ever consulted for a
 *   learner the mission cannot place.
 *
 * NULL WHERE NEITHER SAYS, which is a legacy cohort whose outputs are all
 * submitted, an unauthorized projection, or a store that has not read yet.
 * Null means "do not answer for the squad", and `decidedCapsuleId` then takes
 * the module exactly as it did before this change.
 *
 * @param {object} state the whole store
 * @returns {number|null}
 */
const selectSquadCurrentCapsuleId = (state) => {
  const fromRead = outputCapsuleIdOf(state?.economy?.output ?? null);
  if (fromRead !== null) return fromRead;
  const outputs = state?.experienceProjection?.projection?.outputs;
  if (!Array.isArray(outputs)) return null;
  const open = outputs.find((output) => output?.submitted !== true);
  return coerceCapsuleId(open?.capsule_id);
};

/**
 * THE ONE ANSWER, EXPORTED FOR THE SURFACES THAT ASK IT OUTSIDE THIS FILE.
 *
 * RAW, in the mission's own type, because that is the value `Dashboard.jsx`
 * has always handed to `capsules.find(c => c.id === capsuleId)` and to every
 * prop below it; coercing it here would change a strict comparison on a page
 * this fix is not about. `selectCurrentCapsuleId` coerces for the projection
 * readers, which is where the keyed index needs it.
 *
 * ITS CALLER IS THE DASHBOARD'S `capsuleId`, which feeds `capsule.week` and
 * therefore the squad header's `week N of M`, the journey card's `LEVEL N` and
 * the squad workspace. Those are the other level indicators on the page, and
 * the point of one exported answer is that they cannot disagree with the band
 * about which level a learner is on (W4-F2.6 fix batch item 1).
 */
export const selectDecidedCapsuleId = (state) =>
  decidedCapsuleId(
    state?.user?.mission,
    selectSquadCurrentCapsuleId(state),
    selectBoundaryCapsuleId(state),
  );

/**
 * THE SERVER'S OWN RECORD THAT THIS LEARNER WALKED THROUGH SHIP ON THE DECIDED
 * CAPSULE (W4-F2.12, Addendum 6 C, backend B2.12).
 *
 * `me/economy.levels[].ship_acknowledged_at` is the CALLER'S stamp and nobody
 * else's: `EconomyReadDTO` reads `capsule_users` filtered to the authenticated
 * user before it builds the rows. It is an ISO 8601 string once the learner has
 * acknowledged, and null until then.
 *
 * IT RETURNS THE STAMP AND NOT A BOOLEAN, so it is a PRIMITIVE input to
 * `selectJourneyInputs` below. `state.economy.levels` is a fresh array on every
 * economy read, and the journey decision is re-derived on every recomputation
 * of that selector; taking the array as an input would put the whole cascade on
 * the economy refresh schedule for a fact that changes once per level. A string
 * compares by value, so the cascade recomputes when the acknowledgement lands
 * and on no other read.
 *
 * `sameCapsuleId` AND NOT `===`, for the reason every other capsule comparison
 * in this file gives: the id crosses a JSON boundary and `"501"` names the same
 * level as `501`.
 *
 * THE SCOPE IS NOT CHECKED HERE. It is checked by the one caller, against the
 * `(learner, challenge)` pair that caller has already resolved, exactly as the
 * walkthrough mirror's scope is.
 */
const serverShipAcknowledgedAt = (state) => {
  const capsuleId = selectDecidedCapsuleId(state);
  if (capsuleId === null || capsuleId === undefined) return null;
  const levels = state?.economy?.levels;
  if (!Array.isArray(levels)) return null;
  const row = levels.find((level) =>
    sameCapsuleId(level?.capsule_id, capsuleId),
  );
  return row?.ship_acknowledged_at ?? null;
};

const selectCurrentCapsuleId = (state) =>
  coerceCapsuleId(selectDecidedCapsuleId(state));

/**
 * The tri-state per output, keyed by coerced capsule id.
 *
 * Precedence is submitted, then current, then upcoming. A submitted fact is a
 * server fact and outranks position: when the learner's own current level is
 * already submitted it renders Submitted, not Current, which is what makes
 * "every output submitted" render zero Current rows. With no mission there is
 * no current position, so every unsubmitted output is Upcoming.
 */
export const selectOutputTriState = createSelector(
  [selectAllOutputs, selectCurrentCapsuleId],
  (outputs, currentCapsuleId) => {
    if (outputs.length === 0) return EMPTY_TRI_STATE;
    const triState = {};
    outputs.forEach((output) => {
      const capsuleId = coerceCapsuleId(output?.capsule_id);
      if (capsuleId === null) return;
      if (output?.submitted === true) {
        triState[capsuleId] = TRI_STATE.SUBMITTED;
        return;
      }
      triState[capsuleId] = sameCapsuleId(capsuleId, currentCapsuleId)
        ? TRI_STATE.CURRENT
        : TRI_STATE.UPCOMING;
    });
    return triState;
  },
);

/**
 * The output belonging to the learner's active level, or null.
 *
 * Positional, not stateful: it is the output at the mission's capsule whether or
 * not that level is already submitted. The tri-state above says how it renders.
 */
export const selectCurrentOutput = createSelector(
  [selectAllOutputs, selectCurrentCapsuleId],
  (outputs, currentCapsuleId) => {
    if (currentCapsuleId === null) return null;
    return (
      outputs.find((output) =>
        sameCapsuleId(output?.capsule_id, currentCapsuleId),
      ) ?? null
    );
  },
);

/**
 * Everything the Build Path drawer needs, and only what the projection can
 * prove. PROJECTION-ONLY by contract: it reads no mission, no tasks, and no
 * capsule-user flags, so the drawer can never imply a position the projection
 * does not own.
 *
 * `isUnauthorized` is the 404 collapse: missing and unreadable are one
 * byte-identical state, and the design renders it with no outputs affordance
 * and no level claim.
 */
export const selectBuildPathState = createSelector(
  [selectProjectionState, selectAllOutputs, selectIsStale],
  (projection, outputs, stale) => ({
    status: projection.status,
    error: projection.error,
    isUnauthorized: projection.error?.kind === "unauthorized",
    stale,
    // The server's assembly time, verbatim. The only honest freshness source.
    generatedAt: projection.generatedAt,
    outputs,
    submittedCount: outputs.filter((output) => output?.submitted === true)
      .length,
    // A loaded projection with zero outputs. NOT the same as "not loaded yet":
    // the design drops the Build Path claim entirely on an empty list, and
    // shows a skeleton while loading.
    isEmpty: projection.status === "succeeded" && outputs.length === 0,
  }),
);

/**
 * Squad members that are current, to the extent the payload allows saying so.
 *
 * Forward-tolerant by design. Today `SquadUserResource` carries no
 * `superseded_at`, so no row can be judged and the owner's collection is
 * returned BY REFERENCE, unfiltered and uncopied: no churn, no false claim of
 * currency. When the backend follow-on adds the marker additively, rows that
 * carry a non-null `superseded_at` drop out here and every consumer of
 * `currentSquadUsers` becomes correct with no further frontend change.
 *
 * Detection is per-key, not per-value, because a present-and-null marker means
 * "current" and an absent marker means "unknowable". Collapsing those two would
 * make the filter fire on a payload it cannot actually judge.
 *
 * `membershipFilterable` says which world the caller is in, so a surface that
 * must not imply currency can tell the difference instead of guessing.
 */
export const selectCurrentSquadUsers = createSelector(
  [(state) => state.tasks.squadUsers],
  (squadUsers) => {
    if (!Array.isArray(squadUsers) || squadUsers.length === 0) {
      return { currentSquadUsers: squadUsers, membershipFilterable: false };
    }
    const filterable = squadUsers.some((squadUser) =>
      Object.prototype.hasOwnProperty.call(squadUser ?? {}, "superseded_at"),
    );
    if (!filterable) {
      return { currentSquadUsers: squadUsers, membershipFilterable: false };
    }
    return {
      currentSquadUsers: squadUsers.filter(
        (squadUser) => (squadUser?.superseded_at ?? null) === null,
      ),
      membershipFilterable: true,
    };
  },
);

/**
 * The composed re-entry snapshot: the projection plus every owner slice the
 * re-entry guidance reads, each by reference.
 *
 * The owner sub-states are passed through UNCOPIED and unreshaped, on purpose.
 * A copy would be a second home for a fact that already has one, and it would
 * go stale the moment its owner updated. Identity equality with the owner is
 * the property the tests pin.
 *
 * `buildingTitle` is the authored title of the current output's first artifact,
 * or null. The null is meaningful: the design renders the literal word Output in
 * that case, and that copy belongs to the rendering surface, not to the store.
 */
export const selectReentrySnapshot = createSelector(
  [
    selectBuildPathState,
    selectCurrentOutput,
    selectOutputTriState,
    (state) => state.user.status,
    selectMission,
    (state) => state.tasks.squadUsers,
    selectCurrentSquadUsers,
    (state) => state.planTasks.sessionTasks,
    (state) => state.credentialClearance,
  ],
  (
    buildPath,
    currentOutput,
    triState,
    userStatus,
    mission,
    squadUsers,
    membership,
    sessionTasks,
    credentialClearance,
  ) => {
    const currentCapsuleId = coerceCapsuleId(currentOutput?.capsule_id);
    return {
      buildPath,
      currentOutput,
      triState,
      // The projection's own level number for the current output. Never
      // renumbered and never inferred: absent when the projection has no row
      // for the learner's capsule.
      currentLevel: currentOutput?.level ?? null,
      currentTriState:
        currentCapsuleId === null ? null : (triState[currentCapsuleId] ?? null),
      buildingTitle: currentOutput?.artifacts?.[0]?.title ?? null,
      userStatus,
      mission,
      // The owner's collection, verbatim. Historical membership today: see the
      // header. Consumers that must not count a moved-out member read
      // `currentSquadUsers` and check `membershipFilterable` first.
      squadUsers,
      currentSquadUsers: membership.currentSquadUsers,
      membershipFilterable: membership.membershipFilterable,
      sessionTasks,
      credentialClearance,
    };
  },
);

/* ------------------------------------------------------------------ *
 * Chip P3: the ONE public next-move selector.
 * ------------------------------------------------------------------ */

/**
 * WHICH CAPSULE THE JOURNEY DECISION IS ABOUT: `decidedCapsuleId`, declared at
 * the top of this file beside `selectCurrentCapsuleId`.
 *
 * IT IS A FUNCTION AND NOT THREE COPIES OF AN EXPRESSION. `selectLearnerAtShip`
 * below has to ask the same question `selectJourneyInputs` asks, because its
 * whole purpose is to say whether a decision the cascade made applies to a
 * given capsule. Two spellings of "which capsule" is two answers waiting to
 * disagree, and the one that disagreed would silently either withhold an ending
 * from the learner who earned it or hand one to a learner who did not. W4-F2.6,
 * F48 is what the third spelling cost while it lived in the projection readers.
 */

/**
 * The cascade's inputs, read from their owners.
 *
 * This is the boundary where store shape becomes the explicit argument list
 * deriveJourneyStep takes. Every expression below is the SAME expression the
 * Dashboard effect used inline (Dashboard.jsx @822fed3), moved and not rewritten,
 * including the ones that look redundant: `read_challenge` is compared against
 * three literals because the API has returned all three, and `capsuleId` falls
 * back from the mission's lesson to its module because a mission rooted before
 * the first lesson opens carries only the module.
 */
export const selectJourneyInputs = createSelector(
  [
    (state) => state.dashboard.capsules,
    (state) => state.user.user,
    (state) => state.user.mission,
    (state) => state.user.status,
    (state) => state.user.capsuleUsers,
    (state) => state.tasks.squadUsers,
    (state) => state.credentialClearance,
    /* AND THE SQUAD'S OWN LEVEL, so the rail and the band cannot disagree about
       a learner with no lesson row (W4-F2.6 fix batch item 1). A primitive, so
       it costs this selector no extra recomputation. */
    selectSquadCurrentCapsuleId,
    /* AND WHERE A LEARNER STANDS AT A LEVEL BOUNDARY, a primitive for the same
       reason. */
    selectBoundaryCapsuleId,
    /**
     * W4-F2.10: THE LEARNER'S OWN SHIP WALKTHROUGHS, read from the store so the
     * derivation recomputes when one is recorded.
     *
     * TWO INPUTS AND NOT ONE OBJECT. The scope key is a primitive and the id
     * list is a stable array reference the slice only replaces when it actually
     * changes, so a ship event on the current scope is the only thing that
     * invalidates this selector through these two. Reading the slice object
     * instead would make every unrelated reducer touch of it a recomputation.
     *
     * OPTIONALLY CHAINED because this is a PUBLIC selector and two test stores
     * in this repo compose a subset of the root reducer. An absent slice reads
     * as "no walkthrough recorded", which is the conservative answer: the
     * learner is offered their own Ship step rather than skipped past it.
     */
    (state) => state.shipWalkthrough?.scopeKey ?? null,
    (state) => state.shipWalkthrough?.capsuleIds,
    /**
     * AND WHETHER THE LEDGER HAS BEEN READ YET (fix batch, MINOR 3).
     *
     * The two inputs above cannot distinguish "walked through nothing" from
     * "not asked yet", and the first render is always the second of those: the
     * hydration is a passive effect and this selector runs during render. So a
     * learner with a recorded walkthrough committed one frame parked at their
     * own Ship step before the mirror filled. This input carries that
     * distinction into `ready`, and an unready decision renders nothing rather
     * than a wrong something.
     *
     * ONLY AN EXPLICIT `false` BLOCKS, which is what the reducer's own initial
     * state carries and nothing else does. An absent slice, or a hand-composed
     * substate that states its answer directly, is a world that has already
     * said what it holds; treating either as "never read" would freeze the
     * cascade of every suite that composes its own store, which is a far larger
     * surface than the one frame this fixes.
     */
    (state) => state.shipWalkthrough?.hasHydrated !== false,
    /**
     * THE CARRY MARK, MIRRORED FROM THE SAME LEDGER RECORD (W4-F2.11 fix batch,
     * Codex MINOR 1). Null where the learner has not been carried, or where the
     * slice is absent, which is the conservative answer: the Check-in line
     * claims nothing.
     */
    (state) => state.shipWalkthrough?.movedForwardCapsuleId ?? null,
    /**
     * W4-F2.12: THE SERVER'S OWN SHIP RECORD, AND THE SCOPE IT BELONGS TO.
     *
     * TWO INPUTS AND BOTH PRIMITIVES, the same shape the walkthrough mirror
     * above uses and for the same two reasons. The stamp is a string so the
     * cascade recomputes when the acknowledgement lands and not on every
     * economy read (see `serverShipAcknowledgedAt`), and the scope key is
     * compared in the combiner against the `(learner, challenge)` pair this
     * selector's own `user` and `challenge` name.
     *
     * THE SCOPE COMPARISON IS NOT DECORATION. `economy` is session only and is
     * reset on `LOGOUT`, but its payload is cleared at the PENDING of the next
     * read, so between a scope change and that dispatch the slice still holds
     * the previous `(learner, challenge)`'s levels. A stamp read out of that
     * window is another learner's acknowledgement, and the cost of believing it
     * is a learner carried out of a level they never walked, which is the whole
     * defect Addendum 5 exists for.
     */
    (state) => state.economy?.scopeKey ?? null,
    (state) => serverShipAcknowledgedAt(state),
    /* Experience switcher (B1): the ACTIVE CONTEXT'S enrollment, not `/me`'s
       singular one, which names only one of a learner's cohorts. The
       challenge and `read_challenge` below come from it. */
    selectActiveCohortUser,
  ],
  (
    capsules,
    user,
    mission,
    status,
    capsuleUsers,
    squadUsers,
    clearance,
    squadCapsuleId,
    boundaryCapsuleId,
    walkthroughScopeKey,
    walkthroughCapsuleIds,
    walkthroughHydrated,
    movedForwardMark,
    economySliceScopeKey,
    serverShipAcknowledgement,
    activeCohortUser,
  ) => {
    const challenge = activeCohortUser?.cohort?.challenges?.[0] ?? null;
    const capsuleId = decidedCapsuleId(
      mission,
      squadCapsuleId,
      boundaryCapsuleId,
    );
    const capsule = capsules?.find((candidate) => candidate.id === capsuleId);
    // Guarded to the same depth the two reads three lines down already use.
    // A capsule row that arrives without `modules` would otherwise throw inside
    // a selector that runs on every Dashboard render, and `selectNextAction` is
    // a PUBLIC selector: a future consumer does not get to inherit the one
    // current caller's luck. Values are unchanged for well-formed payloads,
    // because `undefined - 1` is NaN and an index of NaN yields undefined.
    const lastModuleIndex = capsule?.modules?.length - 1;
    const capsuleUser = capsuleUsers?.find((cu) => cu.capsule_id === capsuleId);
    const isCredentialJourney =
      resolveJourneyVariant({ challenge, capsuleId: capsule?.id }) ===
      JOURNEY_VARIANT.CREDENTIAL;

    // The credential gate inputs, in the SAME shape Dashboard's own
    // credentialGateInputs memo builds, so the effective status this selector
    // derives and the one the spine renders cannot disagree. The harness pins
    // that equality rather than trusting the two copies to stay in step.
    const credentialMissionOpened =
      isCredentialJourney &&
      clearance?.capsuleId === capsule?.id &&
      clearance?.data?.act_state?.mission_opened === true;
    // Sprint 3 Chip C. The terminal final exam beat's own state, behind the SAME
    // capsule guard the line above uses: the clearance slice wipes and refills on
    // a capsule change, so a payload keyed to another capsule must not decide
    // this capsule's identity. `findTerminalExamBeat` is the one definition of
    // which beat that is, imported rather than re-derived. Null covers both "no
    // terminal exam in this capsule" and "clearance has not landed yet", and
    // deriveJourneyStep reads both as a plain submitted level.
    const credentialExamState =
      isCredentialJourney && clearance?.capsuleId === capsule?.id
        ? (findTerminalExamBeat(clearance?.data)?.state ?? null)
        : null;
    // The OWNER'S collection, unfiltered, and deliberately not
    // selectCurrentSquadUsers. This predicate reads only the CALLER'S own
    // quests, and the caller of their own dashboard is current by definition,
    // so membership filtering cannot change the answer. It could only change
    // the expression, and this expression is the cascade's, moved verbatim.
    // Same reasoning on the same payload: a squad-user row whose `user` relation
    // has not loaded throws on `.user.id`, and a loaded user with no `quests`
    // throws on `.quests.some`. Neither changes a well-formed answer.
    const hasIncompleteOwnTask = (squadUsers || []).some((squadUser) =>
      squadUser?.user?.id === user?.id
        ? (squadUser?.user?.quests ?? []).some(
            (quest) => quest?.status_id !== 5,
          )
        : false,
    );
    /**
     * HELD AT A LEVEL BOUNDARY: the decision is about the capsule BEFORE the
     * one the mission states (`selectBoundaryCapsuleId`), and the payload's
     * `isTaskCreated` belongs to the STATED capsule, so it says nothing about
     * this one. Read here it sent a learner back to Plan on a level they had
     * finished, or fired the carry on a squadmate's stamp.
     *
     * THE PLAN GATE READS AS PASSED BECAUSE OF WHERE THE LEARNER IS, NOT
     * BECAUSE A PLAN IS PROVED. The server moves the position only after the
     * learner's progress report for this capsule is in (`hasReport`), and the
     * report is the last gate before Ship on both ladders, so the learner's
     * place is Ship. That is all it shows: the server does not check quests
     * for a completed module with a blank reflection prompt (`checkQuests` is
     * false there), and on a v6 board `isTaskCreated` means
     * `plan_completed_at`, so the boundary is no proof that a plan or a task
     * exists.
     */
    const heldAtBoundary = heldAtBoundaryOf(mission, boundaryCapsuleId);
    const gateInputs = {
      missionOpened: credentialMissionOpened,
      moduleCompleted: !!capsule?.modules?.[lastModuleIndex]?.is_completed,
      reflectionPresent:
        capsule?.modules?.[lastModuleIndex]?.reflection !== undefined,
      taskCreated: heldAtBoundary || !!mission?.isTaskCreated,
      allTasksComplete: !hasIncompleteOwnTask,
      reportSubmitted: !!capsuleUser?.milestone_report_submitted,
      deliverablesSubmitted: !!capsuleUser?.deliverables_submitted,
    };

    /**
     * HAS THIS LEARNER WALKED THROUGH SHIP ON THIS CAPSULE, PER THE CLIENT
     * LEDGER? (W4-F2.10.)
     *
     * THIS IS THE OFFLINE CACHE HALF, and since W4-F2.12 it is no longer the
     * whole answer: the server's own record is read below and either source
     * alone opens the gate. It stays first in the file because it is the half
     * that is true in the same commit as the learner's own ship event, and the
     * server's arrives on the read after it.
     *
     * THE SCOPE IS CHECKED HERE AND NOT ASSUMED. The mirror holds one scope at a
     * time, and on a shared device the account can change with the same capsule
     * selected. Comparing the held key against the key this selector's own
     * `user` and `challenge` name is what stops learner A's walkthrough deciding
     * learner B's rail; an unhydrated or mismatched scope answers false, and the
     * hydration effect in `Dashboard.jsx` is what resolves it on the next commit.
     *
     * `sameCapsuleId` AND NOT `includes`. Capsule ids cross a JSON boundary and
     * the shared coercion contract is the one answer to "is this the same
     * capsule" everywhere else in this file; a raw `includes` would read `"101"`
     * and `101` as two levels and offer Ship twice.
     */
    const walkthroughScopeMatches =
      walkthroughScopeKey !== null &&
      walkthroughScopeKey ===
        shipWalkthroughScopeKey({
          userId: user?.id,
          challengeId: challenge?.id,
        });
    const shipWalkthroughLedgerRecorded =
      walkthroughScopeMatches &&
      (walkthroughCapsuleIds ?? []).some((held) =>
        sameCapsuleId(held, capsuleId),
      );

    /**
     * AND THE SERVER'S OWN ANSWER, WHICH IS READ FIRST (W4-F2.12, Addendum 6 C).
     *
     * ADDENDUM 5 DISCLOSED THIS AS A COST AND QUEUED THE FIX. The client ledger
     * is the only per learner, per level record of "I walked through Ship" that
     * existed when F2.10 shipped, and it lives in one browser's
     * `localStorage`. A learner who cleared their storage, or opened the
     * platform on a school's second device, was offered a Ship step they had
     * already walked and watched a level ending they had already seen. Backend
     * B2.12 made the record durable, `capsule_users.ship_acknowledged_at`, and
     * this is the read of it.
     *
     * THE SCOPE IS COMPARED, EXACTLY AS THE MIRROR'S IS. See the input's own
     * note: the economy slice holds the previous learner's levels for the
     * window between a scope change and the next read's pending.
     *
     * TRUTHINESS AND NOT A PARSE. The contract is "an ISO 8601 string once
     * acknowledged, null until then", and the question this gate asks is
     * whether the record EXISTS. A client that parsed the stamp would be
     * deciding a learner's rail on clock agreement between two machines, and
     * would gain nothing: there is no second acknowledgement to order it
     * against. The empty string is excluded because it is neither.
     */
    const economyScopeMatches =
      economySliceScopeKey !== null &&
      economySliceScopeKey ===
        economyScopeKey({ userId: user?.id, challengeId: challenge?.id });
    /**
     * AND THE SAME STAMP ON THE LEARNER'S OWN `capsule_users` ROW, which is
     * where the economy read takes it from (`EconomyReadDTO`). A LEGACY cohort
     * never reads `levels[]`, so before this the server's record reached the
     * ladder only on an economy cohort and a legacy learner on a second device
     * was offered a Ship step they had walked. The row is written on every
     * cohort by the same three ship tails (`SHIP_ACKNOWLEDGEMENT`), and
     * `CapsuleUserResource` serves it. It is the same fact, so it joins the
     * same `OR`; the row must be this learner's, and the rows this slice holds
     * are read for `user.id` and cleared on `LOGOUT`.
     */
    const ownStampRow = capsuleUsers?.find((row) =>
      sameCapsuleId(row?.capsule_id, capsuleId),
    );
    const ownRowStamp =
      ownStampRow &&
      (ownStampRow.user_id === undefined ||
        ownStampRow.user_id === null ||
        Number(ownStampRow.user_id) === Number(user?.id))
        ? ownStampRow.ship_acknowledged_at
        : null;
    const shipAcknowledgedOnServer =
      (economyScopeMatches &&
        typeof serverShipAcknowledgement === "string" &&
        serverShipAcknowledgement.length > 0) ||
      (typeof ownRowStamp === "string" && ownRowStamp.length > 0);

    /**
     * ONE ANSWER, TWO SOURCES, AND THE COMBINATION IS AN `OR` ON PURPOSE.
     *
     * Both are monotonic claims about the same event: this learner walked
     * through Ship on this level, once, and nothing ever un-walks it. So the
     * server's record cannot be allowed to OVERRIDE the ledger downward, and
     * the brief says so ("server null plus ledger true still passes"). A null
     * from the server means one of three things and none of them is a denial:
     * the read has not landed yet, the cohort has no economy at all (a legacy
     * cohort reads no `levels[]`, so the ledger is still the whole answer
     * there), or the write is still in flight behind an event the learner has
     * already performed. Reading a null as "has not walked" would send a
     * learner back through a Ship step mid page life, which is the direction
     * `pastOwnShip` already refuses to fail in.
     *
     * SO "SERVER FIRST" MEANS THE SERVER ANSWERS ON ITS OWN. A fresh browser
     * with an empty ledger and a stamped level lands on the next level with no
     * replay, which is the whole point of the chip; the ledger remains the
     * offline cache underneath it.
     *
     * ONE EFFECTIVE ANSWER AND NOT TWO INPUTS TO THE DERIVATION. Three
     * consumers read this fact: the stock ladder's Ship gate, the credential
     * rail's level boundary (`isCredentialLevelComplete`) and
     * `selectShipWalkthroughRecorded`, which `Dashboard.jsx`'s credential
     * terminal effect reads. Combining the two sources at each of them would be
     * three spellings of one question, and this file's own rule is that the day
     * two spellings disagree is the day one of them carries a learner out of a
     * level the other says they are still in.
     */
    const shipWalkthroughRecorded =
      shipAcknowledgedOnServer || shipWalkthroughLedgerRecorded;

    /**
     * WAS THIS LEARNER CARRIED PAST PLAN AND WORK ON THIS CAPSULE? (W4-F2.11
     * fix batch, Codex MINOR 1.)
     *
     * THE SAME SCOPE COMPARISON AND THE SAME CAPSULE COMPARISON THE WALKTHROUGH
     * ABOVE USES, for the same two reasons: a shared school device switches
     * accounts with the same capsule selected, and capsule ids cross a JSON
     * boundary, so `sameCapsuleId` is the one answer to "is this the same
     * level" in this file.
     *
     * IT IS A RECORD AND NOT A RECONSTRUCTION. The chip as built asked whether
     * the learner has a task of their own on this capsule RIGHT NOW, which is a
     * fact about the present standing in for an event in the past: a deleted or
     * moved quest made the surface tell a learner who had walked Plan and Work
     * that the squad had carried them. The mark is written at the carry itself
     * (`deriveJourneyStep`'s write-through shortcut) and nothing else can
     * produce it.
     */
    const movedForwardRecorded =
      walkthroughScopeMatches && sameCapsuleId(movedForwardMark, capsuleId);

    return {
      /* AND THE WALKTHROUGH MIRROR HAS BEEN READ (fix batch, MINOR 3). It is
         part of READINESS and not a fourth gate, because it is the same kind
         of fact the other two are: an input the decision reads that is filled
         asynchronously after mount. In production it is filled long before the
         capsules land, so this costs the page no commit it did not already
         spend; what it buys is that the FIRST committed frame can never park a
         recorded learner at Ship.

         W4-F2.12 DOES NOT ADD THE ECONOMY READ TO THIS LIST, AND THAT IS A
         DECISION. The server's ship record is the AUTHORITY but it is not a
         precondition: a legacy cohort never reads `levels[]` at all, so
         waiting for it would freeze that cohort's cascade for ever, and an
         economy cohort's first read lands after the capsules do. The ledger is
         the offline cache underneath it, and the direction this gate fails in
         while the read is out is the conservative one the Ship gate already
         documents: the learner is offered their own Ship step, never carried
         past it. */
      ready: !!capsule && !!capsuleUsers && walkthroughHydrated,
      usesV4Experience: usesV4Chain(challenge),
      isCredentialJourney,
      credentialEffectiveStatus: isCredentialJourney
        ? deriveCredentialEffectiveStatus({
            reduxStatus: status,
            ...gateInputs,
          })
        : status,
      credentialMissionOpened,
      /* W4-F2.11, addendum 6 C: the credential rail's level boundary now asks
         for the learner's own ship walkthrough, exactly as the stock ladder's
         Ship gate does. `gateInputs` is the shape
         `deriveCredentialEffectiveStatus` reads and it deliberately does NOT
         carry the walkthrough: the effective status is about which rung the
         learner is on, and Ship is a rung they stand on either way. The
         boundary is the only question the second fact changes. */
      credentialGatesComplete: isCredentialLevelComplete({
        ...gateInputs,
        shipWalkthroughRecorded,
      }),
      credentialExamState,
      status,
      week: capsule?.week ?? null,
      hasReadChallenge:
        activeCohortUser?.read_challenge === 1 ||
        activeCohortUser?.read_challenge === true ||
        activeCohortUser?.read_challenge === "1",
      readObjective: !!capsuleUser?.read_objective,
      moduleCompleted: gateInputs.moduleCompleted,
      reflectionPresent: gateInputs.reflectionPresent,
      taskCreated: gateInputs.taskCreated,
      reportSubmitted: gateInputs.reportSubmitted,
      deliverablesSubmitted: gateInputs.deliverablesSubmitted,
      shipWalkthroughRecorded,
      hasIncompleteOwnTask,
      movedForwardRecorded,
      hasNextCapsule: !!mission?.nextCapsule,
    };
  },
);

/**
 * selectNextAction: the learner's next move, decided once.
 *
 * `{ stepId, status, label, destinationKind, sideEffects }`. The Dashboard
 * cascade effect dispatches `sideEffects` IN ORDER and the re-entry context line
 * reads `stepId` off the same object, so the sentence a learner reads and the
 * step the cascade settles on can never describe different work.
 */
export const selectNextAction = createSelector(
  [selectJourneyInputs],
  (inputs) => deriveJourneyStep(inputs),
);

/**
 * HAS THIS LEARNER WALKED THROUGH SHIP ON THE CAPSULE THE DECISION IS ABOUT?
 * (W4-F2.11, addendum 6 C.)
 *
 * ONE READ OF THE LEDGER MIRROR, AND THIS IS IT. `Dashboard.jsx`'s credential
 * terminal effect needs the same fact the ladder's Ship gate needs, and the
 * scope comparison that makes the fact safe on a shared device lives inside
 * `selectJourneyInputs` (the held key against the key this learner and this
 * challenge name). A second reader would be a second chance to forget the
 * comparison, so the effect reads the input the derivation reads rather than
 * the slice.
 *
 * IT IS ABOUT THE DECIDED CAPSULE, like every other input in that object. The
 * terminal effect asks about `capsuleId`, which is the capsule the page is
 * rendering and the capsule the derivation is run for, so the two agree by
 * construction; if they ever stop agreeing the effect is asking about a level
 * it is not on and the answer it wants is the conservative one, which is what
 * a mismatched scope already returns.
 */
export const selectShipWalkthroughRecorded = createSelector(
  [selectJourneyInputs],
  (inputs) => inputs.shipWalkthroughRecorded,
);

/**
 * THE LEARNER'S DECIDED STEP, IF THE DECISION IS ABOUT THIS CAPSULE.
 *
 * Both Ship questions below are this one lookup plus a different comparison, so
 * the lookup is written once. Null means the question does not apply to the
 * capsule asked about, and both selectors read that as false.
 *
 * IT CALLS THE DERIVATION AND DOES NOT RESTATE IT. `selectNextAction` is the
 * ONE public next-move selector and nothing else may re-derive a learner's
 * position (the no-copy rule at the head of this file). A gate that walked the
 * ladder itself would be a second ladder, and the day the two disagreed the
 * rail would say one thing and the ending another.
 *
 * IT ANSWERS ONLY FOR THE CAPSULE THE LEARNER IS STANDING ON, and null for
 * every other, WHICH IS THE CONSERVATIVE ANSWER AND IS DELIBERATE. The cascade
 * decides one capsule: the mission's. The rows it reads that are not per
 * capsule (`mission.isTaskCreated`, the learner's own open quests) belong to
 * that capsule and to no other, so running the decision against a different
 * capsule's `capsule_users` row would be running it against a mixture.
 *
 * @param {object} state the whole store
 * @param {number|string|null|undefined} capsuleId the capsule the fact is about
 * @returns {string|null} a JOURNEY_STEP value, or null
 */
const decidedStepFor = (state, capsuleId) => {
  if (capsuleId === null || capsuleId === undefined) return null;
  if (
    !sameCapsuleId(
      capsuleId,
      decidedCapsuleId(
        state?.user?.mission,
        selectSquadCurrentCapsuleId(state),
        selectBoundaryCapsuleId(state),
      ),
    )
  ) {
    return null;
  }
  return selectNextAction(state).stepId;
};

/**
 * MAY THIS LEARNER PLAY AN ENDING FOR THIS CAPSULE, AT THE MOMENT OF THE EVENT?
 *
 * W3-F2 ruling A1 (Amir, 2026-09-11). The squad's closing fact and the
 * learner's own position are facts about two different people, and the wave
 * that shipped welded them together: a learner standing on level 1 whose
 * squadmate had shipped the FINAL output was shown `challenge complete.` under
 * their journey and played the ending from the drawer's already-submitted tail.
 * In Amir's words, "it's going to be a premature celebration moment if someone
 * else submits it and they're like, wait, I didn't even get to that step yet."
 *
 * TWO QUESTIONS, TWO SELECTORS (W3-F3 ruling A6 rule 1, 2026-09-11). This one
 * is the LIVE gate and it keeps `ship`, because the learner acknowledging in
 * the drawer IS the learner standing on Ship: the already-submitted tail emits
 * the event the instant its POST resolves and dispatches the row write after,
 * so at the event the store still says Ship. Narrowing this would refuse the
 * acknowledging learner their own ending. Whether the SETTLED record is theirs
 * on arrival is the other question, `selectLearnerPastShip` below.
 *
 * AT OR PAST SHIP IS THREE IDENTITIES, NOT ONE. `ship` is the learner standing
 * on the final node with the deliverable still out; `submitted` is the level
 * closed; `exam` is the credential rail's settled Ship with a terminal exam in
 * play. `journeyIsSettled` is the shipped predicate for the last two, imported
 * rather than respelled for the same reason as above.
 *
 * THE SHORTCUT THAT SUGGESTS ITSELF -- "their own row says
 * `deliverables_submitted`, so they are past Ship" -- is exactly the shortcut
 * that produced the defect this gate exists for, and it still is: the drawer's
 * skip tail WRITES that flag, so the learner who was handed a premature ending
 * had it set while standing on their take. The ladder is asked, never the row.
 *
 * W4-F2.10 GAVE THAT PARAGRAPH A SECOND AUTHOR. Wave 4 B2's reader floor stamps
 * the row for every member of the squad's roster snapshot, so the shortcut is now
 * wrong for a learner who has touched nothing at all. The ladder's Ship gate
 * carries the correction (`deriveJourneyStep`'s `pastOwnShip`), which means this
 * selector answers true for a squadmate standing AT Ship with the squad's output
 * already in -- which is exactly the learner the drawer's Next button belongs to,
 * and exactly the learner Addendum 5 rule 2 says must reach their own ending.
 *
 * @param {object} state the whole store
 * @param {number|string|null|undefined} capsuleId the capsule the fact is about
 * @returns {boolean}
 */
export const selectLearnerAtShip = (state, capsuleId) => {
  const stepId = decidedStepFor(state, capsuleId);
  return stepId === JOURNEY_STEP.SHIP || journeyIsSettled(stepId);
};

/**
 * IS THIS CAPSULE CLOSED FOR THIS LEARNER, SO THE SETTLED RECORD IS THEIRS?
 *
 * W3-F3 ruling A6 (Amir, 2026-09-11), from his third playground test as
 * `malcervantes@test.org`: a learner standing on the last level's Ship node,
 * own deliverable still out, squadmate's output already in, arriving to a card
 * whose body was the record. Ruling A5's takeover had removed the nodes and the
 * drawer with them, so the acknowledgement addendum 1 rule 3 asks for was
 * unreachable. Amir: "Challenge Complete shouldn't appear until I can pass the
 * ship stage, just like we did the last fix, right?"
 *
 * SO OWNERSHIP IS POSITION ALONE, AND `ship` IS NOT IT. `submitted` is the
 * level closed; `exam` is the credential rail's settled Ship. Standing on Ship
 * with the deliverable still out is not ownership, whatever the squad has done.
 *
 * ONE LADDER, TWO COMPARISONS. This selector and the one above share
 * `decidedStepFor` and neither re-derives anything: the day they disagreed
 * about where a learner is standing would be the day one of them was reading a
 * second ladder.
 *
 * @param {object} state the whole store
 * @param {number|string|null|undefined} capsuleId the capsule the fact is about
 * @returns {boolean}
 */
export const selectLearnerPastShip = (state, capsuleId) =>
  journeyIsSettled(decidedStepFor(state, capsuleId));

/* ------------------------------------------------------------------ *
 * W3-F1: the three readers of the authenticated-self completion read.
 * ------------------------------------------------------------------ */

/**
 * May the completion sequence render at all?
 *
 * THE SERVER'S FLAG, AND FALSE UNTIL IT HAS SAID OTHERWISE (brief section 3
 * item 11). `REACT_APP_COMPLETION_SEQUENCE` is a BUNDLING boundary: it decides
 * whether the code is in the bundle, never whether it runs. Rollback is a
 * config change plus `config:cache` on the server with no frontend rebuild, and
 * that only works if this is the one gate every surface reads.
 *
 * It is deliberately not a `createSelector`: it reads one boolean off one
 * slice, and memoizing an identity function is a cache that can only be a place
 * for a stale answer to live.
 */
export const selectCompletionEnabled = (state) =>
  state.completion?.enabled === true;

/**
 * The three door facts, or null while the read has not answered for this scope.
 *
 * NULL AND "NO DOOR" ARE DIFFERENT ANSWERS and both are preserved: null is the
 * platform not having answered yet, and a payload whose three doors are all
 * unavailable is the platform answering that there is no door. The record
 * renders no control in the second case and does not render at all in the
 * first, which is the same rule every beat follows.
 */
export const selectCompletionDoors = (state) => state.completion?.doors ?? null;

/**
 * The caller's own check-in rows: `{ capsule_id, milestone_report_submitted_at }`.
 *
 * THE TIMESTAMP IS THE PREDICATE, NEVER THE BOOLEAN BESIDE IT. That is what
 * every enforcement layer reads (migration `2026_07_29_120010`), the Boolean is
 * the rail's, and the two can disagree. The count itself is taken in `beats.js`
 * so the reader and the counter are one function; this selector's only job is
 * to hand over the rows the caller owns, from the SELF read rather than from
 * the squad-shared projection.
 */
export const selectLearnerCheckIns = (state) =>
  Array.isArray(state.completion?.checkIns) ? state.completion.checkIns : [];
